import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { commentActions } from './index';
import request from 'utils/request';
import {
  selectCardData,
  selectFilterOptions,
  selectSelectedFilter
} from './selectors';
import { findFirstSelectableOption } from '../helper';
import { selectUserProfile } from 'app/pages/Layout/slice/selectors';

// Utility function to call filter API with parameters from state
function* callFilterAPI() {
  const cardData = yield select(selectCardData);
  const loggedInUser = yield select(selectUserProfile);
  const { issueId, momentType } = cardData;
  yield call(fetchFilterOptionsSaga, {
    payload: {
      issueId: issueId,
      momentType: momentType
    },
    type: 'comment/fetchFilterOptions'
  });
}

function* fetchFilterOptionsApi(
  issueId: string,
  momentType: string,
  uid: string
) {
  try {
    const response = yield call(
      request,
      `${process.env.REACT_APP_API_V2_URL}/comments/filter-dropdown?issueId=${issueId}&momentType=${momentType}`
    );
    if (response.error) throw new Error(response.message);
    return response;
  } catch (error) {
    console.error('Failed to fetch filter options:', error);
    throw error;
  }
}

// API call to fetch section options
function* fetchSectionOptionsApi(momentType: string) {
  const response = yield call(
    request,
    `${process.env.REACT_APP_API_V2_URL}/comments/section-dropdown/${momentType}`
  );
  if (response.error) throw new Error(response.message);
  return response;
}

function* fetchFilterOptionsSaga(
  action: PayloadAction<{ issueId: string; momentType: string }>
) {
  try {
    const { issueId, momentType } = action.payload;
    const loggedInUser = yield select(selectUserProfile);
    // Fetch filter options from API
    const response = yield call(
      fetchFilterOptionsApi,
      issueId,
      momentType,
      loggedInUser?.uid
    );

    // Update state with fetched options
    yield put(commentActions.setFilterOptions(response?.sections));
    const findTotalCount = () => {
      if (response?.sections && response.sections.length > 0) {
        const totalCommentsItem = response.sections.find(
          item => item.id === 'totalComments'
        );
        return totalCommentsItem?.commentCount || 0;
      }
      return 0;
    };
    const badgeCount = findTotalCount();
    yield put(commentActions.setBadgeCount(badgeCount));
    // Check if there's already a selected filter
    const currentSelectedFilter = yield select(selectSelectedFilter);

    // Set "All" as the default selected filter only if no filter is currently selected
    if (
      !currentSelectedFilter &&
      response?.sections &&
      response.sections.length > 0
    ) {
      // Create an "All" option
      const allOption = {
        id: 'totalComments',
        label: 'All',
        name: 'All',
        type: 'item'
      };

      // Dispatch action to set "All" as selected filter
      yield put(
        commentActions.filterChanged({
          key: 'totalComments',
          item: allOption,
          filterLabel: 'All'
        })
      );
    }

    yield put(commentActions.setLoadingFilterOptions(false));
  } catch (error) {
    console.error('❌ Failed to fetch filter options:', error);

    yield put(commentActions.setLoadingFilterOptions(false));
    yield put(
      commentActions.setError(
        error instanceof Error
          ? error.message
          : 'Failed to fetch filter options, using defaults'
      )
    );
  }
}

function* fetchSectionOptionsSaga(action: PayloadAction<string>) {
  try {
    const issueId = action.payload;

    const response = yield call(fetchSectionOptionsApi, issueId);
    // Update state with fetched options
    yield put(commentActions.setSectionOptions(response?.sections));

    // Set the first section option as the default selected section
    if (response?.sections && response.sections.length > 0) {
      const firstSection = findFirstSelectableOption(response.sections);

      if (firstSection) {
        // Dispatch action to set first section as selected
        yield put(
          commentActions.sectionChanged({
            key: firstSection.id,
            item: firstSection,
            sectionLabel: firstSection.label || firstSection.name
          })
        );
      }
    }
  } catch (error) {
    console.error('❌ Failed to fetch section options:', error);
    yield put(
      commentActions.setError(
        error instanceof Error
          ? error.message
          : 'Failed to fetch section options, using defaults'
      )
    );
  }
}

function* fetchCommentsSaga(action: PayloadAction<string>) {
  try {
    yield put(commentActions.setLoading(true));
    const issueId = action.payload;
    const response = yield call(
      request,
      `${process.env.REACT_APP_API_V2_URL}/comments/comment/${issueId}`
    );
    if (response.error) throw new Error(response.message);
    yield put(commentActions.setComments(response));
  } catch (error) {
    yield put(
      commentActions.setError(
        error instanceof Error ? error.message : 'Something went wrong'
      )
    );
  } finally {
    // Always reset loading state
    yield put(commentActions.setLoading(false));
  }
}

function* submitCommentSaga(action: PayloadAction<any>) {
  // Preserve the original skeleton comment ID for proper replacement
  const skeletonCommentId = action.payload.id;
  try {
    yield put(
      commentActions.setApiResponseStatus({ status: null, message: null })
    ); // reset at start
    const cardData = yield select(selectCardData);
    const loggedInUser = yield select(selectUserProfile);

    const { issueId, momentType, issueName } = cardData;
    let commentData = {
      ...action.payload,
      momentType: momentType,
      issueId: issueId,
      issueName: issueName,
      id: ''
    };
    const response = yield call(
      request,
      `${process.env.REACT_APP_API_V2_URL}/comments/comment`,
      {
        method: 'POST',
        body: JSON.stringify(commentData),
        headers: { 'Content-Type': 'application/json' }
      },
      {
        showCustomError: true // Enable custom error handling in saga
      }
    );
    if (response.error) throw new Error(response.message);
    // Replace skeleton comment with real comment from API
    yield put(
      commentActions.setCommentAdded({
        comment: response,
        skeletonId: skeletonCommentId,
        readByMention: false
      })
    );

    // yield put(
    //   commentActions.updateFilterCommentCount({
    //     section: action.payload.section,
    //     sectionCategory: action.payload.sectionCategory,
    //     increment: 1,
    //     filterOptions: filterOptions
    //   })
    // );
    yield call(callFilterAPI);
    // yield put(
    //   commentActions.setApiResponseStatus({
    //     status: 'SUCCESS',
    //     message: 'Comment added successfully!'
    //   })
    // );
  } catch (error) {
    // Remove the skeleton comment since API failed
    yield put(commentActions.removeSkeletonComment(skeletonCommentId));
    yield put(
      commentActions.setApiResponseStatus({
        status: 'ERROR',
        message: 'Failed to submit comment'
      })
    );
  }
}

function* replySubmitCommentSaga(action: PayloadAction<any>) {
  // Preserve the original skeleton reply ID for proper replacement
  const skeletonReplyId = action.payload.id;
  const { parentCommentId } = action.payload;
  try {
    yield put(
      commentActions.setApiResponseStatus({ status: null, message: null })
    ); // reset at start
    const cardData = yield select(selectCardData);
    let replyCommentData = {
      ...action.payload,
      issueName: cardData.issueName,
      momentType: cardData.momentType,
      issueId: cardData.issueId,
      // section: '',
      // sectionCategory: '',
      id: parentCommentId
    };
    const response = yield call(
      request,
      `${process.env.REACT_APP_API_V2_URL}/comments/comment`,
      {
        method: 'POST',
        body: JSON.stringify(replyCommentData),
        headers: { 'Content-Type': 'application/json' }
      },
      {
        showCustomError: true // Enable custom error handling in saga
      }
    );
    if (response.error) throw new Error(response.message);
    // Add reply to the parent comment instead of adding it as a new top-level comment
    yield put(
      commentActions.setReplyAdded({
        reply: response,
        parentCommentId: parentCommentId,
        skeletonId: skeletonReplyId
      })
    );
  } catch (error) {
    // Remove the skeleton reply since API failed
    yield put(
      commentActions.removeSkeletonReply({
        replyId: skeletonReplyId,
        parentCommentId: parentCommentId
      })
    );
    yield put(
      commentActions.setApiResponseStatus({
        status: 'ERROR',
        message: 'Failed to submit reply'
      })
    );
  }
}

function* deleteCommentSaga(
  action: PayloadAction<{
    commentId: string;
    userId: string;
    commentSection: string;
    commentSectionCategory: string;
  }>
) {
  const { commentId, commentSectionCategory, commentSection } = action.payload;

  try {
    const filterOptions = yield select(selectFilterOptions);
    yield put(
      commentActions.setApiResponseStatus({ status: null, message: null })
    ); // reset at start

    // Make DELETE API call
    const response = yield call(
      request,
      `${process.env.REACT_APP_API_V2_URL}/comments/comment/${commentId}`,
      {
        method: 'DELETE'
      },
      {
        showCustomError: true // Enable custom error handling in saga
      }
    );

    if (
      response.error ||
      !response.deletedId ||
      response.deletionType !== 'COMMENT'
    ) {
      throw new Error('Failed to delete comment');
    }
    // Update state to remove the deleted comment
    yield put(commentActions.setCommentDeleted(response?.deletedId));
    yield call(callFilterAPI);
    // Decrement filter comment count after successful comment deletion
    // yield put(
    //   commentActions.updateFilterCommentCount({
    //     section: commentSection,
    //     sectionCategory: commentSectionCategory,
    //     increment: -1,
    //     filterOptions: filterOptions
    //   })
    // );
  } catch (error) {
    // Remove skeleton state since delete API failed
    yield put(commentActions.removeDeleteSkeleton(commentId));
    yield put(
      commentActions.setApiResponseStatus({
        status: 'ERROR',
        message: 'Failed to delete comment'
      })
    );
  }
}

function* deleteReplySaga(
  action: PayloadAction<{
    commentId: string;
    userId: string;
    parentCommentId: string;
  }>
) {
  const { commentId, userId, parentCommentId } = action.payload;
  try {
    yield put(
      commentActions.setApiResponseStatus({ status: null, message: null })
    ); // reset at start

    // Make DELETE API call
    const response = yield call(
      request,
      `${process.env.REACT_APP_API_V2_URL}/comments/comment/${parentCommentId}?commentReplyId=${commentId}`,
      {
        method: 'DELETE'
      },
      {
        showCustomError: true // Enable custom error handling in saga
      }
    );

    if (
      response.error ||
      !response.deletedId ||
      response.deletionType !== 'REPLY'
    ) {
      throw new Error('Failed to delete Reply');
    }
    // Update state to remove the deleted comment
    yield put(
      commentActions.setReplyDeleted({
        replyId: response?.deletedId,
        parentCommentId: parentCommentId
      })
    );
  } catch (error) {
    // Remove skeleton state from reply since delete API failed
    yield put(
      commentActions.removeReplyDeleteSkeleton({
        commentId,
        parentCommentId
      })
    );
    yield put(
      commentActions.setApiResponseStatus({
        status: 'ERROR',
        message: 'Failed to delete Reply'
      })
    );
  } finally {
    // Always reset loading state
    yield put(commentActions.setLoading(false));
  }
}

function* editCommentSaga(
  action: PayloadAction<{
    commentId: string;
    text: string;
    commentMentions: any[];
    userId: string;
    editedAt: string;
    commentType: string;
    section: string;
    sectionCategory: string;
    communicationMode: string | null;
  }>
) {
  const {
    commentId,
    text,
    commentMentions,
    userId,
    editedAt,
    commentType,
    section,
    sectionCategory,
    communicationMode
  } = action.payload;
  try {
    yield put(
      commentActions.setApiResponseStatus({ status: null, message: null })
    ); // reset at start
    const cardData = yield select(selectCardData);
    let editCommentData = {
      text,
      commentMentions,
      userId,
      timestamp: editedAt,
      commentReplyId: '',
      commentType: commentType, //EASY debugging
      section: section,
      sectionCategory: sectionCategory,
      communicationMode,
      issueName: cardData.issueName,
      issueId: cardData.issueId
    };
    // Make PATCH API call
    const response = yield call(
      request,
      `${process.env.REACT_APP_API_V2_URL}/comments/comment/${commentId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(editCommentData),
        headers: { 'Content-Type': 'application/json' }
      },
      {
        showCustomError: true // Enable custom error handling in saga
      }
    );
    if (response.error) throw new Error(response.message);
    // Update state with edited comment
    yield put(
      commentActions.setCommentEdited({
        commentId,
        text,
        commentMentions,
        editedAt: editedAt,
        communicationMode: communicationMode
      })
    );
  } catch (error) {
    // Remove edit skeleton state since API failed
    yield put(commentActions.removeEditSkeleton(commentId));
    yield put(
      commentActions.setApiResponseStatus({
        status: 'ERROR',
        message: 'Failed to edit comment'
      })
    );
  }
}

function* editReplySaga(
  action: PayloadAction<{
    commentId: string;
    text: string;
    commentMentions: any[];
    userId: string;
    editedAt: string;
    commentReplyId: string;
    commentType: string;
    section: string;
    sectionCategory: string;
    communicationMode: string | null;
  }>
) {
  const {
    commentId,
    text,
    commentMentions,
    userId,
    editedAt,
    commentReplyId,
    commentType,
    section,
    sectionCategory,
    communicationMode
  } = action.payload;
  try {
    yield put(
      commentActions.setApiResponseStatus({ status: null, message: null })
    ); // reset at start
    const cardData = yield select(selectCardData);
    let editReplyData = {
      text,
      commentMentions,
      userId,
      timestamp: editedAt,
      commentReplyId: commentReplyId,
      commentType: commentType, //EASY debugging
      section: section,
      sectionCategory: sectionCategory,
      communicationMode,
      issueName: cardData.issueName,
      issueId: cardData.issueId
    };
    // Make PATCH API call
    const response = yield call(
      request,
      `${process.env.REACT_APP_API_V2_URL}/comments/comment/${commentId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(editReplyData),
        headers: { 'Content-Type': 'application/json' }
      },
      {
        showCustomError: true // Enable custom error handling in saga
      }
    );
    if (response.error) throw new Error(response.message);
    // Update state with edited comment
    yield put(
      commentActions.setReplyEdited({
        commentId, //Main comment id
        commentReplyId, //Reply Id
        text,
        commentMentions,
        editedAt: editedAt,
        communicationMode: communicationMode
      })
    );
  } catch (error) {
    // Remove edit skeleton state from reply since API failed
    yield put(
      commentActions.removeReplyEditSkeleton({
        commentId,
        replyId: commentReplyId
      })
    );
    yield put(
      commentActions.setApiResponseStatus({
        status: 'ERROR',
        message: 'Failed to edit reply'
      })
    );
  }
}

function* markStatusSaga(
  action: PayloadAction<{
    commentId: string;
    status: 'OPEN' | 'RESOLVED' | 'PENDING';
    userId: string;
    timestamp: string;
  }>
) {
  try {
    const { commentId, status, userId, timestamp } = action.payload;
    yield put(
      commentActions.setApiResponseStatus({ status: null, message: null })
    ); // reset at start

    // Make PATCH API call to update comment status
    const response = yield call(
      request,
      `${process.env.REACT_APP_API_V2_URL}/comments/comment/${commentId}/status`,
      {
        method: 'PUT',
        body: JSON.stringify({ status, userId, timestamp }),
        headers: { 'Content-Type': 'application/json' }
      },
      {
        showCustomError: true // Enable custom error handling in saga
      }
    );
    if (response.error || !response.status)
      throw new Error('Failed to update comment status');
    // Update state with new comment status
    yield put(
      commentActions.setCommentStatus({
        commentId,
        status
      })
    );

    // Update resolved count based on status change
    // const filterOptions = yield select(selectFilterOptions);
    // if (status === 'RESOLVED') {
    //   // Increase resolved count by 1
    //   yield put(
    //     commentActions.updateMarkAsResolvedCount({
    //       filterOptions,
    //       increment: 1
    //     })
    //   );
    // } else if (status === 'OPEN') {
    //   // Decrease resolved count by 1 (moving from resolved back to open)
    //   yield put(
    //     commentActions.updateMarkAsResolvedCount({
    //       filterOptions,
    //       increment: -1
    //     })
    //   );
    // }

    yield call(callFilterAPI);
  } catch (error) {
    yield put(
      commentActions.setApiResponseStatus({
        status: 'ERROR',
        message: `Failed to update comment status`
      })
    );
  }
}

function* markAsReadSaga(
  action: PayloadAction<{
    commentId: string;
    uid: string;
    markAsRead: boolean;
    timestamp: string;
  }>
) {
  try {
    const { commentId, uid, markAsRead, timestamp } = action.payload;
    const cardData = yield select(selectCardData);
    const { issueId, momentType } = cardData;
    yield put(
      commentActions.setApiResponseStatus({ status: null, message: null })
    ); // reset at start

    // Make PUT API call to mark comment as read
    const response = yield call(
      request,
      `${process.env.REACT_APP_API_V2_URL}/comments/comment/${commentId}/mention/read-status`,
      {
        method: 'PUT',
        body: JSON.stringify({ uid, markAsRead, timestamp }),
        headers: { 'Content-Type': 'application/json' }
      },
      {
        showCustomError: true // Enable custom error handling in saga
      }
    );
    if (response.error || !response.success) throw new Error(response.message);
    // Update state with read timestamp
    yield put(
      commentActions.setCommentMarkedAsRead({
        commentId,
        readTimestamp: response?.readByMentionTimestamp
      })
    );
  } catch (error) {
    yield put(
      commentActions.setApiResponseStatus({
        status: 'ERROR',
        message:
          error instanceof Error
            ? error.message
            : 'Failed to mark comment as read'
      })
    );
  }
}

// Root saga
export function* commentSaga() {
  yield takeLatest(
    commentActions.fetchFilterOptions.type,
    fetchFilterOptionsSaga
  );
  yield takeLatest(
    commentActions.fetchSectionOptions.type,
    fetchSectionOptionsSaga
  );
  yield takeLatest(commentActions.fetchComments.type, fetchCommentsSaga);
  yield takeLatest(commentActions.submitComment.type, submitCommentSaga);
  yield takeLatest(
    commentActions.replySubmitComment.type,
    replySubmitCommentSaga
  );
  yield takeLatest(commentActions.deleteComment.type, deleteCommentSaga);
  yield takeLatest(commentActions.deleteReply.type, deleteReplySaga);
  yield takeLatest(commentActions.editComment.type, editCommentSaga);
  yield takeLatest(commentActions.editReply.type, editReplySaga);
  yield takeLatest(commentActions.markStatus.type, markStatusSaga);
  yield takeLatest(commentActions.markAsRead.type, markAsReadSaga);
}

export default commentSaga;
